// src/screens/Section21_1_4.js
import React, { useEffect, useState, useRef } from "react";
import { FileText } from 'lucide-react';
import apiService from "../config";
import html2pdf from "html2pdf.js";
import { useLanguage } from "../StoreContext/LanguageContext";

const HighwaySection21_1_4 = () => {
    const { language, translations } = useLanguage();
    const [formData, setFormData] = useState({});
    const [phoneError, setPhoneError] = useState("");
    const [landData, setLandData] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editValues, setEditValues] = useState({});
    const [isEditing, setIsEditing] = useState(false); // Change from editModalOpen
    const [loading, setLoading] = useState(false);
    const [usersLAO, setusersLAO] = useState([]);
    const notificationContentRef = useRef(null);
    const [isBhusampadanEdited, setIsBhusampadanEdited] = useState(false);
    const [loadingRecords, setLoadingRecords] = useState(false);

    // 🔹 Get projectId & proposalId from localStorage
    const userData = JSON.parse(localStorage.getItem("userData")) || {};
    const projectId = userData.default_project;
    const proposalId = userData.default_proposal;

    const ROLES = {
        COLLECTOR: 1,
        LAO: 3,
        SAMANVAY: 6,
        PROJECT_INCHARGE: 18,
        DATA_ENTRY: 14
    };

    const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

    useEffect(() => {
        const fetchRecords = async () => {
            try {
                setLoadingRecords(true);

                if (projectId && proposalId) {
                    await fetchData();
                    await fetchOwnerData();
                }
            } catch (err) {
                console.error("Error fetching Section 21(4) data:", err);
            } finally {
                setLoadingRecords(false);
            }
        };

        fetchRecords();
    }, [projectId, proposalId]);

    const fetchData = async () => {
        try {

            if (!projectId || !proposalId) return;

            const result = await apiService.getProposalById(projectId, proposalId);

            if (result.status === 200) {
                setFormData(result.data || {});
            }
        } catch (err) {
            console.error("Error fetching Section 21(4) data:", err);
        }
    };

    const fetchOwnerData = async () => {
        try {
            if (!projectId || !proposalId) return;

            const result = await apiService.get("getAllOwner", {
                projectId: projectId,
                proposalId: proposalId,
                isActive: 1
            });

            if (result.status === 200 && Array.isArray(result.landAcquisition)) {
                setLandData(result.landAcquisition || []);
            }
        } catch (err) {
            console.error("Error fetching Section 21(4) data:", err);
        }
    };

    const handleDownloadPDF = () => {
        if (notificationContentRef.current) {
            const opt = {
                margin: 10,
                filename: "Section21_1_4.pdf",
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            };
            html2pdf().set(opt).from(notificationContentRef.current).save();
        }
    };
    const handlePrint = () => {
        if (!landData || landData.length === 0) {
            alert("No landholder data available.");
            return;
        }

        const printContent = document.getElementById("notice-content")?.innerHTML;
        if (!printContent || printContent.trim() === "") {
            alert("No content available to print.");
            return;
        }

        const win = window.open("", "", "height=900,width=1200");
        win.document.write(`<html><head><title>Section 21(1)(4) Notice</title>`);
        win.document.write(`
    <style>
       @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }
      table {
        border-collapse: separate !important;
        width: 100%;
        margin-top: 10px;
      }
      th, td {
        border: 1px solid black;
        padding: 4px;
        text-align: center;
      }

       h2, h3 {
        text-align: center;
        margin: 0;
      }

      /* ✅ Alignment fixes */
      .text-2xl { font-size: 1.8rem; }
      .text-center, .center-text { text-align: center !important; }
      .text-left, .left-text { text-align: left !important; }
      .text-right { text-align: right !important; }
      .mx-auto { margin-left: auto !important; margin-right: auto !important; }
      .ml-3 { margin-left: 12px !important; }
      .mt-6 { margin-top : 10px !important; }
      .mt-5 { margin-top : 6px !important; }
      .mt-12 { margin-top : 12px !important; }
      .mt-20 { margin-top : 50px !important; }
      .mr-5 { margin-right : 2rem !important; }
      .font-bold { font-weight: bold; }
      .flex { display: flex !important; }
.inline-flex { display: inline-flex !important; }
.justify-between { justify-content: space-between !important; }
.justify-center { justify-content: center !important; }
.justify-end { justify-content: flex-end !important; }
.items-center { align-items: center !important; }
  .underline { text-decoration: underline; }

      /* ✅ Page breaks */
      .page-break { page-break-before: always; }

      /* ✅ Fix emblem size */
      img[src*="Emblem_of_India"] {
        width: 80px !important;
        height: auto !important;
        display: block;
        margin: 0 auto 8px auto !important;
      }

      /* ✅ Prevent squishing */
      #notice-content {
        width: 100% !important;
        max-width: 180mm;
        margin: 0 auto;
      }
    </style>
  </head><body>`);
        win.document.write(printContent);
        win.document.write("</body></html>");
        win.document.close();
        win.print();
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, ""); // ✅ remove non-digits
        if (value.length > 10) return; // ✅ block extra digits

        setFormData((prev) => ({
            ...prev,
            section_21_4_mobile_no: value,
        }));

        // ✅ Validation as user types
        if (value.length === 0) {
            setPhoneError("");
        } else if (/^[6-9]\d{9}$/.test(value)) {
            setPhoneError(""); // valid mobile
        } else if (/^0\d{9}$/.test(value)) {
            setPhoneError(""); // valid landline
        } else {
            setPhoneError("कृपया वैध 10 अंकी मोबाइल (6-9 पासून सुरू) किंवा लँडलाइन (0 पासून सुरू) क्रमांक भरा.");
        }
    };

    // Replace your openEdit function with this:
    const openEdit = () => {
        setIsEditing(true);
    };

    // 🔹 Save edits using updateProposalPublish
    const handleSave = async () => {
        try {
            const payload = { ...editValues };
            await apiService.updateProposalPublish(proposalId, payload);

            setFormData((prev) => ({ ...prev, ...payload }));
            setEditModalOpen(false);
        } catch (err) {
            console.error("Error saving Section 21(4):", err);
        }
    };
    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d)) return "";
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };


    // Convert ISO or SQL date → dd-mm-yyyy
    const toDisplayDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d)) return "";
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`; // dd-mm-yyyy
    };

    // Convert dd-mm-yyyy → yyyy-mm-dd for backend
    const toApiDate = (displayDate) => {
        if (!displayDate) return null;
        const [day, month, year] = displayDate.split("-");
        return `${year}-${month}-${day}`;
    };

    // fetch LAO users
    const fetchusersLAO = async () => {
        const role = 3;
        try {
            const response = await apiService.getAssignLAO(role, projectId, proposalId);
            console.log('LAO users:', response);
            if (response.status === 200 && response.data) {
                setusersLAO(Array.isArray(response.data) ? response.data : []);
            } else {
                setusersLAO([]);
            }
        } catch (error) {
            console.error('Error fetching LAO users:', error);
            setusersLAO([]);
        } finally {

        }
    };

    useEffect(() => {
        fetchusersLAO();
    }, [projectId, proposalId]);

    const laoName = usersLAO?.[0]?.full_name || 'N/A';
    const laoDesi = usersLAO?.[0]?.designation || 'N/A';
    const laoEmail = usersLAO?.[0]?.email || 'N/A';

    // Helper functions for date formatting
    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d)) return "";
        return d.toISOString().split("T")[0]; // yyyy-mm-dd for input
    };

    // Helper functions for default content
    const getDefaultSection1Content = () => {
        return `भूसंपादन प्रकरण क्रमांक ${formData.section_21_4_bhusampadan_no || ''} मध्ये समुचित शासनाने उक्त अधिनियमाचे कलम -19 खालील घोषणा निर्गमित करुन खालीलप्रमाणे प्रसिध्द केलेली आहे.`;
    };

    const getSection1ContentForEdit = () => {
        if (formData.section1_content &&
            formData.section1_content.trim() !== '' &&
            formData.section1_content !== 'N/A' &&
            formData.section1_content !== 'null' &&
            formData.section1_content !== 'undefined') {
            return formData.section1_content;
        }
        return getDefaultSection1Content();
    };

    const getDefaultTip = () => {
        return "उपरोक्त घोषणेनुसार परिशिष्टामध्ये अधिसूचित जमिनीची कामासाठी गरज आहे.";
    };

    const getTipForEdit = () => {
        if (formData.section_21_4_tip &&
            formData.section_21_4_tip.trim() !== '' &&
            formData.section_21_4_tip !== 'N/A' &&
            formData.section_21_4_tip !== 'null' &&
            formData.section_21_4_tip !== 'undefined') {
            return formData.section_21_4_tip;
        }
        return getDefaultTip();
    };

    // Add this helper function to get the display value for bhusampadan number
    const getBhusampadanDisplayValue = () => {
        if (formData.section_21_4_bhusampadan_no) {
            return formData.section_21_4_bhusampadan_no;
        }
        return formData.section_11_bhusampadan_no || '';
    };

    // Add this helper function to get the value to send in payload
    const getBhusampadanPayloadValue = () => {
        if (isBhusampadanEdited && formData.section_21_4_bhusampadan_no) {
            return formData.section_21_4_bhusampadan_no;
        }
        return formData.section_11_bhusampadan_no || '';
    };

    // Updated input change handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Track if bhusampadan field is being edited
        if (name === "section_21_4_bhusampadan_no") {
            setIsBhusampadanEdited(true);
        }
        // Special case for date field
        if (name === "section_21_4_date") {
            setFormData((prev) => ({
                ...prev,
                [name]: value ? new Date(value).toISOString() : ""   // store ISO date
            }));
        } else if (name === "section_21_4_claim_filing_date") {
            setFormData((prev) => ({
                ...prev,
                [name]: value ? new Date(value).toISOString() : ""   // store ISO date
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Updated submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // 🔹 Phone validation (Mobile or Landline India)
            if (formData.section_21_4_mobile_no) {
                const phone = formData.section_21_4_mobile_no.trim();
                const isMobile = /^[6-9]\d{9}$/.test(phone);   // mobile: starts 6-9, 10 digits
                const isLandline = /^0\d{9}$/.test(phone);     // landline: starts 0, 10 digits
                if (!isMobile && !isLandline) {
                    alert("कृपया योग्य मोबाइल (10 digits, starts 6-9) किंवा लँडलाइन (starts with 0, 10 digits) क्रमांक भरा.");
                    setLoading(false);
                    return;
                }
            }

            const payload = {
                section_21_4_office_name: formData.section_21_4_office_name,
                section_21_4_branch_name: formData.section_21_4_branch_name,
                section_21_4_email: formData.section_21_4_email,
                section_21_4_mobile_no: formData.section_21_4_mobile_no,
                section_21_4_address: formData.section_21_4_address,
                section_21_4_notification: formData.section_21_4_notification,
                section_21_4_date: formData.section_21_4_date,
                section_21_4_bhusampadan_no: getBhusampadanPayloadValue(),
                section_21_4_claim_filing_place: formData.section_21_4_claim_filing_place,
                section_21_4_claim_filing_date: formData.section_21_4_claim_filing_date,
            };
            await apiService.updateProposalPublish(proposalId, payload);
            setIsEditing(false);
            // Show success message if needed
        } catch (err) {
            console.error("Error saving Section 21(4):", err);
            // Show error message if needed
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get field values
    const getFieldValue = (fieldName, defaultValue = '') => {
        return formData[fieldName] || defaultValue;
    };

    // Helper function to format dates for display
    const formatDate = (dateStr) => {
        if (!dateStr) return "Not Set";
        return formatDisplayDate(dateStr);
    };

    if (loadingRecords) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex justify-center items-center h-80">
                    <div className="text-center animate-float">
                        <div className="inline-flex relative">
                            <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                            <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <FileText className="h-6 w-6 text-purple-400 animate-pulse" />
                            </div>
                        </div>
                        <p className="text-gray-400 mt-4">कलम 21(1)(4) लोड करत आहे...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="flex gap-4 mb-4">
                <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                    {translations[language].Download}
                </button>

                {/* {userRole === ROLES.LAO && ( */}
                {/* Data Entry */}
                {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                    < button
                        onClick={openEdit}
                        className="px-4 py-2 bg-green-600 text-white rounded"
                    >
                        {translations[language].edit}
                    </button>
                )}
            </div>

            {/* Document */}

            <div id="notice-content" style={{ margin: "10mm" }}>
                {(() => {
                    // Create array of individual owners with their land details
                    const individualOwners = [];

                    landData.forEach((landAcq) => {
                        if (landAcq.owners && landAcq.owners.length > 0) {
                            landAcq.owners.forEach((owner) => {
                                individualOwners.push({
                                    owner: owner,
                                    landDetails: landAcq
                                });
                            });
                        }
                    });

                    return individualOwners.map((ownerData, ownerIdx) => (
                        <div
                            key={ownerIdx}
                            className="bg-white shadow-lg p-8 rounded border border-gray-300"
                            style={{
                                fontFamily: "'Noto Sans Devanagari', calibri, sans-serif",
                                width: "210mm",
                                minHeight: "297mm",
                                margin: "0 auto",
                                pageBreakAfter: ownerIdx < individualOwners.length - 1 ? "always" : "auto"
                            }}
                        >
                            {/* Header */}
                            <img src="/Emblem_of_India.svg" alt="Emblem" className="mx-auto mb-2 w-20" />
                            <div className="text-center font-bold">महाराष्ट्र शासन</div>
                            <div className="text-center font-bold mb-1">महसूल व वन विभाग</div>
                            <div className="text-center font-bold text-lg mb-1 mt-1">
                                <span>{formData.section_21_4_office_name}</span>
                            </div>
                            <div className="text-center font-bold">
                                शाखा :- <span>{formData.section_21_4_branch_name}</span>
                            </div>
                            <div className="text-center font-bold">
                                <span>{formData.section_21_4_address}</span>
                            </div>

                            {/* Contact Section */}
                            <div className="text-xs mt-4">
                                <div className="flex justify-between">
                                    <div>
                                        ई-मेल :-{" "}
                                        <span className="font-bold text-black-700">
                                            {formData.section_21_4_email || laoEmail}
                                        </span>
                                    </div>
                                    <div>
                                        दूरध्वनी क्रमांक :-{" "}
                                        <span className="font-bold text-black-700">
                                            {formData.section_21_4_mobile_no}
                                        </span>
                                    </div>
                                </div>
                                <hr className="border-black border-2 my-1" />
                                <div className="flex justify-between">
                                    <div>
                                        <span>{formData.section_21_4_notification}</span>
                                    </div>
                                    <div>
                                        दिनांक :-{" "}
                                        <span>{formatDisplayDate(formData.section_21_4_date)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Notice heading */}
                            <div className="text-center text-base font-bold underline mt-5">
                                भूमिसंपादन, पुनर्वसन व पुनर्वसाहत करतांना उचित भरपाई मिळण्याचा व
                                पारदर्शकतेचा हक्क अधिनियम, 2013 कलम 21 (1), (4) अन्वये दयावयाची नोटिस.
                            </div>

                            <br />
                            प्रति,<br />

                            {/* Display only the current owner */}
                            <div className="ml-3">
                                {ownerData.owner.owner_name}<br />
                            </div>
                            <br />

                            {/* <div className="">रा. {formData.village_name} रा. {formData.taluka_name} जि.बुलढाणा</div>
              <div className="text-center">***</div> */}

                            {/* Notice content */}
                            <div className="mt-6 text-sm">
                                &nbsp;&nbsp;&nbsp;&nbsp;भूसंपादन प्रकरण क्रमांक {getBhusampadanDisplayValue()} मध्ये
                                समुचित शासनाने उक्त अधिनियमाचे कलम -19 खालील घोषणा निर्गमित करुन
                                खालीलप्रमाणे प्रसिध्द केलेली आहे.<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;1) शासनाचे अधिकृत संकेतस्थळावर (वेबसाईटवर) दिनांक <span className="ml-1">{formatDisplayDate(formData.section19_government_notice_board)} </span>; <br></br>
                                &nbsp;&nbsp;&nbsp;&nbsp;2) जिल्हाधिकारी कार्यालय, बुलढाणा दिनांक   <span className="ml-1">{formatDisplayDate(formData.section19_collector_office)}</span>;<br></br>
                                &nbsp;&nbsp;&nbsp;&nbsp;3) महाराष्ट्र शासन राजपत्र दिनांक   <span className="ml-1">{formatDisplayDate(formData.section19_published_gazette)}</span>;<br></br>
                                &nbsp;&nbsp;&nbsp;&nbsp;4) दोन स्थानिक वृत्तपत्र, संबंधित ग्रामपंचायत/ उपविभागिय अधिकारी कार्यालय / तहसिल कार्यालय, बाधित क्षेत्रातील संबंधित गावातील सार्वजनिक ठिकाणी/ चावडीचे ठिकाणी प्रसिध्द करण्यात आलेले आहे. <br></br>

                                <p className="mt-2">&nbsp;&nbsp;&nbsp;&nbsp;उपरोक्त घोषणेनुसार परिशिष्टामध्ये अधिसूचित जमिनीची <span>{formData.project_name}</span> बुडीत क्षेत्राच्या कामासाठी गरज आहे आणि सदर शेती ताब्यात घेण्याचा शासनाचा निश्चय आहे. त्याअनुषंगाने जमिनीच्या मोबदल्याबाबत काही हरकती/दावे असल्यास {formData.section_21_4_claim_filing_place} येथे आपण स्वत: किंवा आपले प्रतिनिधी/ अभिकर्त्यामार्फत दिनांक {formatDisplayDate(formData.section_21_4_claim_filing_date)} पर्यंत कार्यालयीन वेळेत आपले म्हणणे मांडण्यास हजर रहावे.</p>
                            </div>

                            {/* Land details table - show only this owner's land */}
                            <div className="mt-6">
                                <div className="text-center font-bold underline">
                                    संपादन करावयाच्या आणि ताब्यात घ्यावयाच्या जमिनीचा तपशिल
                                </div>
                                <table className="border border-black border-collapse w-full text-xs mt-2">
                                    <thead>
                                        <tr>
                                            <th className="border p-1">अ.क्र</th>
                                            <th className="border p-1">भूधारकाचे नांव</th>
                                            <th className="border p-1">गाव /तालुका /जिल्हा</th>
                                            <th className="border p-1">गट क्रमांक</th>
                                            <th className="border p-1">लागवड योग्य क्षेत्र (हे.आर.)</th>
                                            <th className="border p-1">पोट खराब क्षेत्र (हे.आर.)</th>
                                            <th className="border p-1">संपादीत जमिनीचे क्षेत्रफळ (हे.आर.)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="border p-1 text-center">1</td>
                                            <td className="border p-1 text-center">{ownerData.owner.owner_name}</td>
                                            <td className="border p-1 text-center">
                                                मौजे {ownerData.owner.village_name}, ता.{ownerData.owner.taluka_name}, जि.{ownerData.owner.dist_name}
                                            </td>
                                            <td className="border p-1 text-center">{ownerData.owner.survey_no}</td>
                                            <td className="border p-1 text-center">{parseFloat(ownerData.owner.owner_irrigable_area || 0).toFixed(2)}</td>
                                            <td className="border p-1 text-center">{parseFloat(ownerData.owner.owner_non_irrigable_area || 0).toFixed(2)}</td>
                                            <td className="border p-1 text-center">
                                                {((ownerData.owner.owner_irrigable_area || 0) + (ownerData.owner.owner_non_irrigable_area || 0)).toFixed(2)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Signature section */}
                            <div className="text-right mt-20 mr-5 text-sm">
                                ({laoName}) <br />
                                {laoDesi}
                            </div>
                        </div>
                    ));
                })()}
            </div>

            {/* Edit Modal */}
            {
                isEditing && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-gray-800">{translations[language].edit} {translations[language].section21_1_4}</h2>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                                    >
                                        ×
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    {/* Editable Fields */}
                                    {/* 1. Header / Office Information */}
                                    <div className="mb-6">
                                        <div className="flex items-center justify-center mb-6">
                                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
                                                <h2 className="font-bold text-lg">Office & Notice Details</h2>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    कार्यालयाचे नाव :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_office_name"
                                                    value={formData.section_21_4_office_name || ''}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    शाखेचे नाव :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_branch_name"
                                                    value={formData.section_21_4_branch_name || ''}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    ई-मेल :
                                                </label>
                                                <input
                                                    type="email"
                                                    name="section_21_4_email"
                                                    value={formData.section_21_4_email ?? ""}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    दूरध्वनी क्रमांक :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_mobile_no"
                                                    value={formData.section_21_4_mobile_no ?? ""}
                                                    onChange={(e) => handlePhoneChange(e)}
                                                    maxLength={10}   // ✅ restrict max digits
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                                <p className="text-sm text-red-500 mt-1">
                                                    {phoneError}
                                                </p>
                                            </div>

                                            <div className="group md:col-span-2">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    पत्ता :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_address"
                                                    value={formData.section_21_4_address || ''}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    कार्यविवरण क्रमांक :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_notification"
                                                    value={formData.section_21_4_notification || ''}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    दिनांक :
                                                </label>
                                                <input
                                                    type="date"
                                                    name="section_21_4_date"
                                                    value={formatDateForInput(formData.section_21_4_date)}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            {/* bhusampadan no */}
                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    भूसंपादन प्रकरण क्रमांक :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_bhusampadan_no"
                                                    value={getBhusampadanDisplayValue()}
                                                    onChange={handleInputChange}
                                                    placeholder={`Default: ${formData.section_11_bhusampadan_no || 'N/A'}`}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                                {/* {!isBhusampadanEdited && formData.section_11_bhusampadan_no && (
                        <p className="text-sm text-blue-600 mt-1">
                          Currently using default value from Section 11
                        </p>
                      )} */}
                                            </div>

                                            <div className="group md:col-span-2">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    दावा दाखल करण्याचे ठिकाण :
                                                </label>
                                                <input
                                                    type="text"
                                                    name="section_21_4_claim_filing_place"
                                                    value={formData.section_21_4_claim_filing_place}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter No"
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                            <div className="group">
                                                <label className="block font-semibold text-gray-700 mb-2 text-base">
                                                    दावा दाखल करण्याचे दिनांक :
                                                </label>
                                                <input
                                                    type="date"
                                                    name="section_21_4_claim_filing_date"
                                                    value={formatDateForInput(formData.section_21_4_claim_filing_date)}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                                                />
                                            </div>

                                        </div>
                                    </div>

                                    {/* Show current notice preview */}
                                    <div className="mb-6 p-4 bg-gray-50 border rounded-lg">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-3">Current Notice Preview</h3>
                                        <div className="max-h-72 overflow-y-auto border rounded p-3 text-sm bg-white">
                                            <div
                                                id="notice-preview"
                                                dangerouslySetInnerHTML={{
                                                    __html: document.getElementById("notice-content")?.innerHTML || "No notice content available."
                                                }}
                                            />

                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-4 border-t pt-6">
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(false)}
                                            className="px-6 py-3 border rounded-xl"
                                        >
                                            {translations[language]?.Cancel}
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl"
                                        >
                                            {loading ? "Saving..." : translations[language].saveChanges}
                                        </button>
                                    </div>
                                </form>


                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default HighwaySection21_1_4;